import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { 
  ArrowLeft, Edit, Trash2, Printer, Download, MapPin, Phone, Globe,
  Check, X, Clock, Users, DollarSign, Languages, Building2,
  AlertTriangle, Lightbulb, FileText, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_COLORS = {
  "Food Security": "bg-amber-100 text-amber-800 border-amber-200",
  "Housing & Shelter": "bg-blue-100 text-blue-800 border-blue-200",
  "Healthcare Access": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Mental Health": "bg-purple-100 text-purple-800 border-purple-200",
  "Substance Use": "bg-rose-100 text-rose-800 border-rose-200",
  "Legal / Immigration": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Employment / Income": "bg-teal-100 text-teal-800 border-teal-200",
  "Transportation": "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Education / ESL": "bg-orange-100 text-orange-800 border-orange-200",
  "Childcare / Family Support": "bg-pink-100 text-pink-800 border-pink-200",
  "Violence / Safety": "bg-red-100 text-red-800 border-red-200",
  "Digital Access": "bg-slate-100 text-slate-800 border-slate-200",
  "Utilities": "bg-yellow-100 text-yellow-800 border-yellow-200"
};

const ResourceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await axios.get(`${API_URL}/resources/${id}`, {
          headers: getAuthHeaders()
        });
        setResource(response.data);
      } catch (error) {
        toast.error('Failed to load resource');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchResource();
  }, [id, getAuthHeaders, navigate]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/resources/${id}`, { headers: getAuthHeaders() });
      toast.success('Resource deleted');
      navigate('/');
    } catch {
      toast.error('Failed to delete resource');
    } finally {
      setDeleting(false);
    }
  }, [id, getAuthHeaders, navigate]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  const handleExportPDF = useCallback(() => {
    if (!resource) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    const margin = 20;
    const lineHeight = 7;
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(resource.resource_name, margin, y);
    y += 10;
    
    // Organization
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(resource.organization, margin, y);
    y += 15;
    
    // Category
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Category: ', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(resource.primary_sdoh_category, margin + 25, y);
    y += lineHeight * 2;
    
    // Description
    doc.setFont('helvetica', 'bold');
    doc.text('Description:', margin, y);
    y += lineHeight;
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(resource.client_facing_description, pageWidth - margin * 2);
    doc.text(descLines, margin, y);
    y += descLines.length * lineHeight + 5;
    
    // Contact Info
    if (resource.phone_number || resource.website || resource.physical_address) {
      doc.setFont('helvetica', 'bold');
      doc.text('Contact Information:', margin, y);
      y += lineHeight;
      doc.setFont('helvetica', 'normal');
      
      if (resource.phone_number) {
        doc.text(`Phone: ${resource.phone_number}`, margin, y);
        y += lineHeight;
      }
      if (resource.website) {
        doc.text(`Website: ${resource.website}`, margin, y);
        y += lineHeight;
      }
      if (resource.physical_address) {
        doc.text(`Address: ${resource.physical_address}`, margin, y);
        y += lineHeight;
      }
      y += 5;
    }
    
    // Cost & Eligibility
    doc.setFont('helvetica', 'bold');
    doc.text('Cost & Eligibility:', margin, y);
    y += lineHeight;
    doc.setFont('helvetica', 'normal');
    doc.text(`Cost: ${resource.cost_to_client}`, margin, y);
    y += lineHeight;
    
    if (resource.eligibility_requirements) {
      const eligLines = doc.splitTextToSize(`Eligibility: ${resource.eligibility_requirements}`, pageWidth - margin * 2);
      doc.text(eligLines, margin, y);
      y += eligLines.length * lineHeight;
    }
    y += 5;
    
    // Languages
    if (resource.languages_offered && resource.languages_offered.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text('Languages:', margin, y);
      y += lineHeight;
      doc.setFont('helvetica', 'normal');
      doc.text(resource.languages_offered.join(', '), margin, y);
      y += lineHeight + 5;
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text('617Reach', margin, doc.internal.pageSize.getHeight() - 10);
    doc.text(new Date().toLocaleDateString(), pageWidth - margin - 30, doc.internal.pageSize.getHeight() - 10);
    
    doc.save(`${resource.resource_name.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF downloaded!');
  }, [resource]);

  const categoryColor = useMemo(
    () => CATEGORY_COLORS[resource?.primary_sdoh_category] || "bg-gray-100 text-gray-800 border-gray-200",
    [resource?.primary_sdoh_category]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5C4023]" />
      </div>
    );
  }

  if (!resource) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header - Hidden in print */}
      <header className="sticky top-0 z-40 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#E0D6C8] no-print">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-btn"
                className="rounded-full hover:bg-[#F3EFE9]"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="font-['Manrope'] text-base tracking-tight"><span className="font-extrabold text-[#5C4023]">617</span><span className="font-medium text-[#C87D3E]">Reach</span></span>
                <span className="text-sm font-medium text-[#5A5A5A] hidden sm:inline">· Resource Details</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrint}
                data-testid="print-btn"
                className="rounded-full hover:bg-[#F3EFE9]"
              >
                <Printer className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
                className="rounded-full hover:bg-[#F3EFE9]"
              >
                <Download className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/edit/${resource.id}`)}
                data-testid="edit-btn"
                className="rounded-full hover:bg-[#F3EFE9]"
              >
                <Edit className="w-5 h-5" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="delete-btn"
                    className="rounded-full hover:bg-red-50 text-[#E07A5F]"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Resource?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete &ldquo;{resource.resource_name}&rdquo;. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      data-testid="confirm-delete-btn"
                      className="bg-[#E07A5F] hover:bg-[#D06045] rounded-full"
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </header>

      {/* Print Header */}
      <div className="print-header hidden print:block px-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-['Manrope']"><span className="font-extrabold">617</span><span className="font-medium">Reach</span></span>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 print-container">
        {/* Title Card */}
        <Card className="rounded-2xl border-[#E0D6C8] mb-6 print-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className={`${categoryColor} rounded-full border`}>
                {resource.primary_sdoh_category}
              </Badge>
              {resource.secondary_sdoh_categories?.map(cat => (
                <Badge key={cat} variant="outline" className="rounded-full text-[#5A5A5A]">
                  {cat}
                </Badge>
              ))}
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope'] mb-2">
              {resource.resource_name}
            </h1>
            
            <div className="flex items-center text-[#5A5A5A] mb-4">
              <Building2 className="w-4 h-4 mr-2" />
              <span>{resource.organization}</span>
            </div>
            
            <p className="text-[#1A1A1A] leading-relaxed">
              {resource.client_facing_description}
            </p>
          </CardContent>
        </Card>

        {/* Contact & Location */}
        <Card className="rounded-2xl border-[#E0D6C8] mb-6 print-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Manrope'] flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-[#5C4023]" />
              Contact & Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {resource.phone_number && (
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3 text-[#888888]" />
                <a href={`tel:${resource.phone_number}`} className="text-[#5C4023] hover:underline">
                  {resource.phone_number}
                </a>
              </div>
            )}
            
            {resource.website && (
              <div className="flex items-center">
                <Globe className="w-4 h-4 mr-3 text-[#888888]" />
                <a href={resource.website} target="_blank" rel="noopener noreferrer" className="text-[#5C4023] hover:underline break-all">
                  {resource.website}
                </a>
              </div>
            )}
            
            {resource.physical_address && (
              <div className="flex items-start">
                <MapPin className="w-4 h-4 mr-3 text-[#888888] mt-0.5" />
                <span>{resource.physical_address}</span>
              </div>
            )}
            
            {resource.neighborhoods_served?.length > 0 && (
              <div>
                <p className="text-sm text-[#888888] mb-1">Neighborhoods Served</p>
                <div className="flex flex-wrap gap-1">
                  {resource.neighborhoods_served.map(n => (
                    <Badge key={n} variant="secondary" className="bg-[#F3EFE9] text-[#5A5A5A] rounded-full">
                      {n}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {resource.zip_codes?.length > 0 && (
              <div>
                <p className="text-sm text-[#888888] mb-1">Zip Codes</p>
                <span className="text-[#5A5A5A]">{resource.zip_codes.join(', ')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Eligibility & Access */}
        <Card className="rounded-2xl border-[#E0D6C8] mb-6 print-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Manrope'] flex items-center">
              <Users className="w-5 h-5 mr-2 text-[#5C4023]" />
              Eligibility & Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center p-3 bg-[#F3EFE9] rounded-xl">
                <DollarSign className="w-5 h-5 mr-3 text-[#5C4023]" />
                <div>
                  <p className="text-xs text-[#888888]">Cost</p>
                  <p className="font-medium text-[#1A1A1A]">{resource.cost_to_client}</p>
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-[#F3EFE9] rounded-xl">
                <FileText className="w-5 h-5 mr-3 text-[#5C4023]" />
                <div>
                  <p className="text-xs text-[#888888]">Referral</p>
                  <p className="font-medium text-[#1A1A1A] flex items-center">
                    {resource.referral_required ? (
                      <><Check className="w-4 h-4 mr-1 text-amber-600" /> Required</>
                    ) : (
                      <><X className="w-4 h-4 mr-1 text-emerald-600" /> Not Required</>
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            {resource.eligibility_requirements && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-1">Eligibility Requirements</p>
                <p className="text-[#5A5A5A]">{resource.eligibility_requirements}</p>
              </div>
            )}
            
            {resource.populations_served?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2">Populations Served</p>
                <div className="flex flex-wrap gap-1">
                  {resource.populations_served.map(p => (
                    <Badge key={p} variant="secondary" className="bg-[#F3EFE9] text-[#5A5A5A] rounded-full">
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {resource.service_modality?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2">Service Types</p>
                <div className="flex flex-wrap gap-1">
                  {resource.service_modality.map(m => (
                    <Badge key={m} className="bg-[#5C4023]/10 text-[#5C4023] border-[#5C4023]/20 rounded-full">
                      {m}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {resource.languages_offered?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center">
                  <Languages className="w-4 h-4 mr-2" />
                  Languages
                </p>
                <div className="flex flex-wrap gap-1">
                  {resource.languages_offered.map(l => (
                    <Badge key={l} variant="outline" className="rounded-full">
                      {l}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Staff Notes - Hidden in print */}
        {(resource.how_to_access || resource.known_barriers || resource.staff_tips || resource.warm_handoff_possible) && (
          <Card className="rounded-2xl border-[#E0D6C8] bg-amber-50/50 staff-notes no-print">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Manrope'] flex items-center text-amber-800">
                <Lightbulb className="w-5 h-5 mr-2" />
                Staff Notes
                <Badge className="ml-2 bg-amber-200 text-amber-800 text-xs">Internal Only</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {resource.warm_handoff_possible && (
                <div className="flex items-center p-3 bg-emerald-100 rounded-xl text-emerald-800">
                  <Check className="w-5 h-5 mr-2" />
                  <span className="font-medium">Warm Handoff Available</span>
                </div>
              )}
              
              {resource.how_to_access && (
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    How to Access
                  </p>
                  <p className="text-[#5A5A5A]">{resource.how_to_access}</p>
                </div>
              )}
              
              {resource.known_barriers && (
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Known Barriers
                  </p>
                  <p className="text-[#5A5A5A]">{resource.known_barriers}</p>
                </div>
              )}
              
              {resource.staff_tips && (
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1 flex items-center">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Staff Tips
                  </p>
                  <p className="text-[#5A5A5A]">{resource.staff_tips}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ResourceDetailPage;
